import {
  ManagedLeadCategory,
  ManagedLeadTransition,
  ManagedLeadUpdateResult,
} from '../../services/api/managed-leads/managedLeads';
import {
  describeManagedLeadWriteFailure,
  isManagedLeadNoteRetryable,
  ManagedLeadWriteAct,
  ManagedLeadWriteFailure,
  ManagedLeadWriteOutcome,
} from './managedLeadFailures';
import { MANAGED_LEAD_CATEGORY_LABELS } from './managedLeadFormatting';
import { getTassoQuintoErrorCode } from '../../services/api/tasso-quinto';

/**
 * What a failed update means for the form: the shared decisions every managed
 * lead write takes, plus the one field only this path owns.
 */
export interface ManagedLeadUpdateFailure extends ManagedLeadWriteFailure {
  /** Message to show under the Nota field when the server blamed it. */
  noteFieldMessage: string | null;
}

/**
 * How the update names itself in the failures it shares with the correction.
 * Only the noun changes: the codes mean the same thing on both paths.
 */
const UPDATE_ACT: ManagedLeadWriteAct = {
  failed:
    "Non è stato possibile salvare l'aggiornamento. I dati inseriti sono stati conservati.",
  invalid: "L'aggiornamento non è valido",
  unauthorized: 'Non sei autorizzato ad aggiornare questa lead.',
  resend: 'Riprova per inviare questo aggiornamento.',
};

/**
 * The parts of the payload the form owns without rendering them as a field of
 * their own. Advancement data is not here on purpose: those labels are the ones
 * the policy published with its descriptors, so the operator reads the same
 * wording in the form and in the rejection.
 */
const FIELD_LABELS: Record<string, string> = {
  note: 'Nota',
  expectedCurrentStage: 'Fase attesa',
  targetStage: 'Fase di destinazione',
};

/**
 * Translates an update failure into what the operator sees and what the form
 * does next, reacting to the stable envelope `code` and never to its message.
 * The codes only an advancement can receive are answered here; everything an
 * update shares with a correction is translated once, in `managedLeadFailures`.
 */
export const describeManagedLeadUpdateFailure = (
  error: unknown,
  /**
   * Semantic field name to the label the policy published for it. It is what
   * turns a rejected `body.fields.requestedAmount` into "Importo richiesto"
   * without the backoffice keeping a vocabulary of its own.
   */
  advancementLabels: Record<string, string> = {}
): ManagedLeadUpdateFailure => {
  const shared = describeManagedLeadWriteFailure(error, UPDATE_ACT, {
    form: advancementLabels,
    other: FIELD_LABELS,
  });
  const withNote = (
    failure: ManagedLeadWriteFailure
  ): ManagedLeadUpdateFailure => ({
    ...failure,
    noteFieldMessage: failure.rejectedFields.includes('note')
      ? 'La nota non rispetta i limiti previsti.'
      : null,
  });

  switch (getTassoQuintoErrorCode(error)) {
    case 'deal_not_updatable':
      return withNote({
        ...shared,
        message: 'Questa lead non è aggiornabile nella fase corrente.',
        retryable: false,
        refetchesDetail: true,
      });
    case 'stage_conflict':
      return withNote({
        ...shared,
        message:
          "La fase della lead è cambiata nel frattempo. Il dettaglio è stato ricaricato: rileggi la fase e invia di nuovo l'aggiornamento.",
        // The expected stage the key was minted with is now wrong: a retry has
        // to be a new attempt against the stage just re-read.
        retryable: false,
        renewsIdempotencyKey: true,
        refetchesDetail: true,
      });
    case 'unsupported_stage_transition':
      return withNote({
        ...shared,
        message:
          'Questo aggiornamento non è supportato per la fase selezionata.',
        retryable: false,
        refetchesDetail: true,
      });
    default:
      return withNote(shared);
  }
};

/**
 * What a `200` means for the update form: the stage change has priority over
 * the Nota and is not reversible, so an applied advancement with a failed Nota
 * is announced as an outstanding step, never as a success.
 */
export type ManagedLeadUpdateOutcome = ManagedLeadWriteOutcome;

const NOTE_NOT_CREATED_MESSAGE =
  'La fase è stata aggiornata ma la nota non è stata salvata. Riprova: verrà ritentata soltanto la nota.';

const REPLAYED_MESSAGE =
  'Questo aggiornamento era già stato registrato: non è stato duplicato.';

const NOTE_ONLY_MESSAGE =
  'Nota salvata. Il dettaglio e le note sono stati aggiornati.';

const DATA_ONLY_MESSAGE = 'Dati di avanzamento aggiornati.';

/**
 * Describes a completed update. Both `target` and `from` are the ones the
 * request was minted for, never re-read from the refreshed detail: once an
 * advancement lands, the lead has already left the stage and the category the
 * operator is being told about.
 */
export const describeManagedLeadUpdateOutcome = (
  result: ManagedLeadUpdateResult,
  target: ManagedLeadTransition,
  from: ManagedLeadCategory
): ManagedLeadUpdateOutcome => {
  if (result.outcome === 'partial_success') {
    return {
      severity: 'warning',
      message: NOTE_NOT_CREATED_MESSAGE,
      retryable: isManagedLeadNoteRetryable(result.note),
    };
  }

  const settled = { severity: 'success', retryable: false } as const;
  if (result.replayed) return { ...settled, message: REPLAYED_MESSAGE };
  const noteMessage = result.note.created ? ' La nota è stata salvata.' : '';
  if (!result.stageUpdated) {
    // The lead has not moved, so the only thing to report is what the update
    // actually wrote: the Advancement data, or nothing but the Nota.
    return {
      ...settled,
      message: result.fieldsUpdated
        ? `${DATA_ONLY_MESSAGE}${noteMessage}`
        : NOTE_ONLY_MESSAGE,
    };
  }

  const stageMessage = `Fase aggiornata a ${target.label}.`;
  // A lead that leaves the section it was listed in is now somewhere else, and
  // the operator has to be told where to find it.
  const movedMessage =
    target.category === from
      ? ''
      : ` La lead compare ora in ${
          MANAGED_LEAD_CATEGORY_LABELS[target.category]
        }.`;

  return {
    ...settled,
    message: `${stageMessage}${noteMessage}${movedMessage}`,
  };
};
